import React, { useState } from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Grid,
} from '@mui/material'
import {
  MailOutline,
  ContentCopy,
  CheckCircleOutline,
  Event as EventIcon,
  AccessTime,
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import { useLocation, useNavigate } from 'react-router-dom'

const BookingConfirmation = ({ companyData }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)

  const { booking, jobTypeName, companyName, companySlug } =
    location.state || {}

  // If no state (direct access), redirect to home
  if (!booking) {
    return (
      <Container maxWidth='sm' sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant='h5' gutterBottom>
          No booking details found
        </Typography>
        <Button variant='contained' onClick={() => navigate('/')}>
          Go to Home
        </Button>
      </Container>
    )
  }

  const formatDate = dateStr => {
    try {
      return format(parseISO(dateStr), 'EEEE do MMMM yyyy')
    } catch {
      return dateStr
    }
  }

  const formatTime = timeStr => {
    if (!timeStr) return ''
    const parts = timeStr.split(':')
    return `${parts[0]}:${parts[1]}`
  }

  const profileUrl = companySlug
    ? `https://book.mygroupbooker.com/${companySlug}`
    : window.location.origin

  const shareMessage = `I'm getting my ${
    jobTypeName || 'annual service'
  } on ${formatDate(booking.date)}${
    booking.currentPrice
      ? `, currently £${booking.currentPrice}${booking.chargeVat ? ' inc VAT' : ''}`
      : ''
  }. The more people booked on the day, the less we pay. Join the group booking here: ${profileUrl}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = shareMessage
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareMessage)}`
  const emailShareUrl = `mailto:?subject=${encodeURIComponent(
    `Join my group booking - ${jobTypeName || 'annual service'}`
  )}&body=${encodeURIComponent(shareMessage)}`

  return (
    <Container maxWidth='md' sx={{ py: { xs: 6, md: 8 } }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
      {/* Booking Summary */}
      <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
        <MailOutline
          sx={{ fontSize: 64, color: 'secondary.main', mb: 2 }}
        />
        <Typography variant='h5' gutterBottom color='primary'>
          Check your email
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
          To confirm your booking, check your email (and spam) for a message
          from <strong>{companyName}</strong> and tap{' '}
          <strong>'Confirm my booking'</strong>.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Booking details */}
        <Box sx={{ textAlign: 'left' }}>
          {jobTypeName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CheckCircleOutline sx={{ color: 'secondary.main', fontSize: 22 }} />
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Service
                </Typography>
                <Typography variant='body1'>{jobTypeName}</Typography>
              </Box>
            </Box>
          )}
          {booking.date && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <EventIcon sx={{ color: 'primary.main', fontSize: 22 }} />
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Date
                </Typography>
                <Typography variant='body1'>
                  {formatDate(booking.date)}
                </Typography>
              </Box>
            </Box>
          )}
          {booking.startTime && booking.endTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <AccessTime sx={{ color: 'primary.main', fontSize: 22 }} />
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Arrival time
                </Typography>
                <Typography variant='body1'>
                  {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                </Typography>
              </Box>
            </Box>
          )}
          {booking.currentPrice && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <Typography
                variant='h6'
                sx={{ color: 'primary.main', fontWeight: 'bold', width: 22, textAlign: 'center' }}
              >
                £
              </Typography>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Current price
                </Typography>
                <Typography variant='body1'>
                  £{booking.currentPrice}{booking.chargeVat ? ' (inc VAT)' : ''}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
      {/* Share Section */}
      <Paper sx={{ p: 4, height: '100%' }}>
        <Typography variant='h6' gutterBottom color='primary'>
          Share with your neighbours
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Remember, the more people on a booking, the cheaper the service. Let
          your neighbours know about your group booking to bring the price down
          for everyone.
        </Typography>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Copy and paste this message to share in your group WhatsApp, Facebook
          or email chain:
        </Typography>

        <Box
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            mb: 2,
            position: 'relative',
          }}
        >
          <Typography variant='body2' sx={{ pr: 5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {shareMessage}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton
              onClick={handleCopy}
              size='small'
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              {copied ? (
                <CheckCircleOutline color='success' />
              ) : (
                <ContentCopy fontSize='small' />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Share on:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant='outlined'
            size='small'
            href={whatsappUrl}
            target='_blank'
            rel='noopener noreferrer'
            sx={{ textTransform: 'none' }}
          >
            WhatsApp
          </Button>
          <Button
            variant='outlined'
            size='small'
            href={facebookUrl}
            target='_blank'
            rel='noopener noreferrer'
            sx={{ textTransform: 'none' }}
          >
            Facebook
          </Button>
          <Button
            variant='outlined'
            size='small'
            href={emailShareUrl}
            sx={{ textTransform: 'none' }}
          >
            Email
          </Button>
        </Box>
      </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}

export default BookingConfirmation
